import { useMemo } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { dayLabel, MAX_SESSIONS, type Draft } from "@heraion/shared";

import { AddDayChip } from "../IntensityGrid/GridAddChips";
import { IconButton } from "../primitives/IconButton";
import type { UiTheme } from "../theme";
import { groupLabels } from "./groupLabels";
import type { V3DayActions } from "./useV3DayActions";
import type { V3Navigation } from "./useV3Navigation";
import type { V3Styles } from "./v3Styles";

// The program's structure, left of the grid on a wide screen (#336 slice 6,
// mock `wideView`): each day with its menu, and under it its exercises, a
// superset's label under the name, as the phone's day. A day selects the day;
// an exercise opens it on the right. «D ＋» while there is room. The actions are the phone's:
// the menu is the day menu, the add is the heatmap's D+.
export function DayStructure({
  theme,
  styles,
  draft,
  nav,
  days,
  resolveName,
  onDayMenu,
}: {
  theme: UiTheme;
  styles: V3Styles;
  draft: Draft;
  nav: V3Navigation;
  days: V3DayActions;
  resolveName: (id: string, snapshot?: string) => string;
  onDayMenu: (sIdx: number) => void;
}) {
  const { t } = useTranslation();
  const s = useMemo(() => makeStyles(theme), [theme]);
  return (
    <View style={s.root}>
      <Text style={s.label} accessibilityRole="header">
        {t("editorV3.grid.structure", { defaultValue: "Structure" })}
      </Text>
      {draft.sessions.map((session, sIdx) => {
        const day = dayLabel(draft, sIdx);
        const current = sIdx === nav.dayIdx;
        const groups = groupLabels(draft, sIdx, t);
        return (
          <View key={sIdx} style={s.day}>
            <View style={s.dayHead}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={day}
                accessibilityState={{ selected: current }}
                onPress={() => nav.selectDay(sIdx)}
                style={({ pressed }) => [s.dayName, current && s.dayCurrent, pressed && s.pressed]}
              >
                <Text style={[s.dayText, current && s.dayTextCurrent]} numberOfLines={2}>
                  {day}
                </Text>
              </Pressable>
              <IconButton
                theme={theme}
                name="more"
                accessibilityLabel={t("editorV3.dayMenuA11y", { day, defaultValue: "{{day}}, day menu" })}
                onPress={() => onDayMenu(sIdx)}
              />
            </View>
            {session.slots.map((slot, slotIdx) => {
              const name = resolveName(slot.exerciseId, slot.exerciseName);
              const tag = slot.group ? groups.get(slot.group.id) : undefined;
              const open = current && nav.openSlotIdx === slotIdx;
              return (
                <Pressable
                  key={slot.id}
                  accessibilityRole="button"
                  accessibilityLabel={[name, tag, day].filter(Boolean).join(", ")}
                  accessibilityState={{ selected: open }}
                  onPress={() => {
                    if (!current) nav.selectDay(sIdx);
                    nav.openExercise(slot.id);
                  }}
                  style={({ pressed }) => [s.exercise, open && s.exerciseOpen, pressed && s.pressed]}
                >
                  <Text style={[s.exerciseText, open && s.exerciseTextOpen]} numberOfLines={2}>
                    {name}
                  </Text>
                  {tag ? <Text style={styles.tag}>{tag}</Text> : null}
                </Pressable>
              );
            })}
          </View>
        );
      })}
      {draft.sessions.length < MAX_SESSIONS ? (
        <View style={s.add}>
          <AddDayChip theme={theme} onPress={days.add} />
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    root: { padding: s.s4, gap: s.s4 },
    label: { color: p.ink3, fontSize: ty.sizes.sm, fontWeight: "700", textTransform: "uppercase" },
    day: { gap: s.s1 },
    dayHead: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    dayName: { flex: 1, minHeight: 44, justifyContent: "center", paddingHorizontal: s.s2, borderRadius: r.r2 },
    // The current day: tinted, and its name bold, never the tint alone.
    dayCurrent: { backgroundColor: p.brand100 },
    dayText: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "600" },
    dayTextCurrent: { color: p.brandText, fontWeight: "800" },
    exercise: { minHeight: 44, justifyContent: "center", paddingLeft: s.s5, paddingRight: s.s2, borderRadius: r.r2 },
    exerciseOpen: { backgroundColor: p.surface2 },
    exerciseText: { color: p.ink2, fontSize: ty.sizes.sm },
    exerciseTextOpen: { color: p.ink, fontWeight: "700" },
    pressed: { backgroundColor: p.surface2 },
    // The chip keeps its own width, as in the heatmap's structure row.
    add: { alignItems: "flex-start" },
  });
}
